'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useDebounceValue } from 'usehooks-ts';
import {
  ListPageLayout,
  excludeArchivedTracePreviews,
} from '@terra/ui-design-system';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  LIST_BADGE_LIFECYCLE_STATE,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { useAppTargetId } from '../../store/projectStore';
import { useTabScopedTracesFilters } from '../../store/tab/tracesTabScopedFilterStore';
import { useArchivedLifecycleFilter } from '../../hooks/useArchivedLifecycleFilter';
import { useInvalidateApplicationDomainTree } from '../../hooks/api/useApplicationDomainTree';
import { useTracesListRefresh } from '../../hooks/useTracesListRefresh';
import {
  useTracesGroupCounts,
  useTracesStatusCounts,
  useTracesTotalCount,
} from '../../hooks/api/useTracesTotalCount';
import { TracesDeleteDialog } from './TracesDeleteDialog';
import { ExecutionsBulkActionsBar } from './executions-header/ExecutionsBulkActionsBar';
import { ExecutionsContextNavigation } from './executions-header/ExecutionsContextNavigation';
import { ExecutionsFilterPanel } from './executions-header/ExecutionsFilterPanel';
import { ExecutionsViewOptionsMenu } from './executions-header/ExecutionsViewOptionsMenu';
import { ExecutionsTable } from './executions-table/ExecutionsTable';
import {
  ExecutionsTableContextProvider,
  type ExecutionsTableContextValue,
} from './executions-table/ExecutionsTableContext';
import {
  buildExecutionsGroupCountsQuery,
  buildExecutionsStatusCountsQuery,
  hasAnyExecutionFilters,
  resolveExecutionsSelectAllState,
} from './executions-table/executions-list.model';
import { useExecutionsActions } from './executions-table/useExecutionsActions';
import { useExecutionsListData } from './executions-table/useExecutionsListData';
import { useExecutionsTableRows } from './executions-table/useExecutionsTableRows';
import { useExecutionsNavigation } from './executions-table/useExecutionsNavigation';
import { useExecutionsScope } from './executions-table/useExecutionsScope';
import { useExecutionsSelection } from './executions-table/useExecutionsSelection';

type TracesContentProps = {
  header?: ReactNode;
  endpointId?: string;
  endpointUrl?: string;
  lifecycleStateOverride?: LifecycleStateFilterDto;
};

export function TracesContent({
  header,
  endpointId,
  endpointUrl,
  lifecycleStateOverride,
}: TracesContentProps) {
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const appTargetId = useAppTargetId();
  const filters = useTabScopedTracesFilters();
  const lifecycleState = lifecycleStateOverride ?? filters.lifecycleState;
  const {
    lifecycleQueryParam,
    showLifecycleSelector,
    areArchivedMutationsDisabled,
  } = useArchivedLifecycleFilter({ lifecycleState, lifecycleStateOverride });
  const invalidateApplicationDomainTree = useInvalidateApplicationDomainTree();
  const [debouncedSearchTerm] = useDebounceValue(filters.searchTerm, 500);
  const scope = useExecutionsScope({ endpointId, endpointUrl });
  const selectionResetKey = [
    appTargetId ?? '',
    debouncedSearchTerm,
    filters.statusFilter.join(','),
    filters.resultFilter.join(','),
    filters.agentFilter.join(','),
    filters.createdSince?.toISOString() ?? '',
    scope.scopeKey,
    lifecycleQueryParam ?? '',
  ].join('-');
  const { totalCount: unfilteredTotalCount, invalidate: invalidateTotalCount } =
    useTracesTotalCount({
      applicationId: appTargetId,
      endpointIds: scope.scopedEndpointIds,
      urlPatterns: scope.scopedUrlPatterns,
      lifecycleState: LIST_BADGE_LIFECYCLE_STATE,
      enabled: !!appTargetId && !scope.isTreeFilterPending,
    });
  const statusCountsQuery = useMemo(
    () =>
      appTargetId
        ? buildExecutionsStatusCountsQuery({
            applicationId: appTargetId,
            searchTerm: debouncedSearchTerm,
            resultFilter: filters.resultFilter,
            agentFilter: filters.agentFilter,
            scopedEndpointIds: scope.scopedEndpointIds,
            scopedUrlPatterns: scope.scopedUrlPatterns,
            createdSince: filters.createdSince,
            lifecycleState: lifecycleQueryParam,
          })
        : null,
    [
      appTargetId,
      debouncedSearchTerm,
      filters.agentFilter,
      filters.createdSince,
      filters.resultFilter,
      lifecycleQueryParam,
      scope.scopedEndpointIds,
      scope.scopedUrlPatterns,
    ],
  );
  const { statusCounts } = useTracesStatusCounts({
    query: statusCountsQuery,
    enabled: !scope.isTreeFilterPending,
  });
  const groupCountsQuery = useMemo(
    () =>
      appTargetId && filters.grouping
        ? buildExecutionsGroupCountsQuery({
            applicationId: appTargetId,
            searchTerm: debouncedSearchTerm,
            statusFilter: filters.statusFilter,
            resultFilter: filters.resultFilter,
            agentFilter: filters.agentFilter,
            scopedEndpointIds: scope.scopedEndpointIds,
            scopedUrlPatterns: scope.scopedUrlPatterns,
            createdSince: filters.createdSince,
            lifecycleState: lifecycleQueryParam,
            groupBy: filters.grouping.field,
          })
        : null,
    [
      appTargetId,
      debouncedSearchTerm,
      filters.agentFilter,
      filters.createdSince,
      filters.grouping,
      filters.resultFilter,
      filters.statusFilter,
      lifecycleQueryParam,
      scope.scopedEndpointIds,
      scope.scopedUrlPatterns,
    ],
  );
  const { groups, hasGroups } = useTracesGroupCounts({
    query: groupCountsQuery,
    enabled: !scope.isTreeFilterPending,
  });
  const { isRefreshing, silentRefreshKey, handleRefresh } =
    useTracesListRefresh({
      applicationId: appTargetId,
      invalidateTotalCount,
      invalidateApplicationDomainTree,
    });
  const listData = useExecutionsListData({
    applicationId: appTargetId,
    scopeKey: scope.scopeKey,
    scopedEndpointIds: scope.scopedEndpointIds,
    scopedUrlPatterns: scope.scopedUrlPatterns,
    isScopePending: scope.isTreeFilterPending,
    searchTerm: debouncedSearchTerm,
    statusFilter: filters.statusFilter,
    resultFilter: filters.resultFilter,
    agentFilter: filters.agentFilter,
    createdSince: filters.createdSince,
    lifecycleState: lifecycleQueryParam,
    sort: filters.sort,
    grouping: filters.grouping,
    silentRefreshKey,
  });
  const tableRows = useExecutionsTableRows({
    traces: listData.traces,
    grouping: filters.grouping,
    groups,
    hasServerGroups: hasGroups,
    hasMore: listData.hasNextPage,
    resetKey: selectionResetKey,
  });
  const navigation = useExecutionsNavigation();
  const selection = useExecutionsSelection({
    resetKey: selectionResetKey,
    selectableTotalCount: listData.selectableTotalCount,
    fetchAllTraces: listData.fetchAllTraces,
  });
  const actions = useExecutionsActions({
    applicationId: appTargetId,
    getSelectedTraces: selection.getSelectedTraces,
    onComplete: handleRefresh,
    onSelectionCleared: selection.handleDeselectAll,
  });
  const hasSelectableRows = useMemo(
    () => excludeArchivedTracePreviews(listData.traces).length > 0,
    [listData.traces],
  );
  const isSelectionLocked =
    actions.isResolvingSelection ||
    actions.isDeleting ||
    actions.isBulkResuming ||
    actions.isBulkRerunning ||
    actions.isBulkAborting;
  const { isTraceSelected, toggleTrace, selectAll } = selection;
  const onSelectAll = useCallback(
    (selected: boolean) => {
      if (!isSelectionLocked) {
        selectAll(selected);
      }
    },
    [isSelectionLocked, selectAll],
  );
  const tableContextValue = useMemo(
    (): ExecutionsTableContextValue => ({
      selectAllState: resolveExecutionsSelectAllState({
        areAllSelected: selection.areAllSelected,
        selectedCount: selection.selectedCount,
      }),
      hasSelectableRows,
      isSelectionLocked,
      isTraceSelected,
      onToggleTraceSelected: toggleTrace,
      onSelectAll,
      resumingTraceIds: actions.resumingTraceIds,
      rerunningTraceIds: actions.rerunningTraceIds,
      abortingTraceIds: actions.abortingTraceIds,
      onResumeTrace: actions.handleResumeTrace,
      onRerunTrace: actions.handleRerunTrace,
      onAbortTrace: actions.handleAbortTrace,
      onDeleteTrace: actions.requestDelete,
    }),
    [
      actions.abortingTraceIds,
      actions.handleAbortTrace,
      actions.handleRerunTrace,
      actions.handleResumeTrace,
      actions.requestDelete,
      actions.rerunningTraceIds,
      actions.resumingTraceIds,
      hasSelectableRows,
      isSelectionLocked,
      isTraceSelected,
      onSelectAll,
      selection.areAllSelected,
      selection.selectedCount,
      toggleTrace,
    ],
  );
  const hasFilters = hasAnyExecutionFilters({
    searchTerm: filters.searchTerm,
    statusFilter: filters.statusFilter,
    resultFilter: filters.resultFilter,
    agentFilter: filters.agentFilter,
    createdSince: filters.createdSince,
    selectableLifecycleState: showLifecycleSelector
      ? filters.lifecycleState
      : null,
  });
  const clearFilters = () => {
    filters.setSearchTerm('');
    filters.setStatusFilter([]);
    filters.setResultFilter([]);
    filters.setAgentFilter([]);
    filters.setCreatedSince(null);
    filters.setLifecycleState(DEFAULT_LIFECYCLE_STATE_FILTER);
  };

  return (
    <ExecutionsTableContextProvider value={tableContextValue}>
      <ListPageLayout
        header={header}
        navigation={
          <ExecutionsContextNavigation
            statusFilter={filters.statusFilter}
            onStatusFilterChange={filters.setStatusFilter}
            statusCounts={statusCounts}
            searchTerm={filters.searchTerm}
            onSearchTermChange={filters.setSearchTerm}
            onRefresh={handleRefresh}
            isLoading={isRefreshing}
            optionsMenu={
              <ExecutionsViewOptionsMenu
                groupByField={filters.grouping?.field ?? null}
                onGroupByFieldChange={filters.setGroupByField}
                areAllGroupsCollapsed={tableRows.areAllGroupsCollapsed}
                onExpandAllGroups={tableRows.expandAllGroups}
                onCollapseAllGroups={tableRows.collapseAllGroups}
              />
            }
          />
        }
        filters={
          <ExecutionsFilterPanel
            statusFilter={filters.statusFilter}
            onStatusFilterChange={filters.setStatusFilter}
            resultFilter={filters.resultFilter}
            onResultFilterChange={filters.setResultFilter}
            agentFilter={filters.agentFilter}
            onAgentFilterChange={filters.setAgentFilter}
            createdSince={filters.createdSince}
            onCreatedSinceClear={() => filters.setCreatedSince(null)}
            isLifecycleEnabled={showLifecycleSelector}
            lifecycleState={lifecycleState}
            onLifecycleStateChange={filters.setLifecycleState}
            hasActiveFilters={hasFilters}
            onClear={clearFilters}
            showFilteredCounter={hasFilters}
            filteredCount={listData.totalCount}
            totalCount={unfilteredTotalCount}
            selection={
              selection.selectedCount > 0 ? (
                <ExecutionsBulkActionsBar
                  selectedCount={selection.selectedCount}
                  resumableCount={selection.resumableCount}
                  rerunableCount={selection.rerunableCount}
                  abortableCount={selection.abortableCount}
                  isBulkResuming={actions.isBulkResuming}
                  isBulkRerunning={actions.isBulkRerunning}
                  isBulkAborting={actions.isBulkAborting}
                  isBulkDeleting={actions.isDeleting}
                  isBusy={isSelectionLocked}
                  mutationsDisabledTooltip={
                    areArchivedMutationsDisabled
                      ? tLifecycle('archivedExecutionActionTooltip')
                      : null
                  }
                  onClearSelection={selection.handleDeselectAll}
                  onResume={() => void actions.bulkResume()}
                  onRerun={() => void actions.bulkRerun()}
                  onAbort={() => void actions.bulkAbort()}
                  onDelete={() => void actions.requestBulkDelete()}
                />
              ) : null
            }
          />
        }
      >
        <ExecutionsTable
          rows={tableRows.rows}
          traceCount={listData.traces.length}
          loadMoreRowIndex={tableRows.loadMoreRowIndex}
          sort={filters.sort}
          grouping={filters.grouping}
          collapsedGroupKeys={tableRows.collapsedGroupKeys}
          isLoading={listData.isInitialLoading}
          isFetchingMore={listData.isFetchingMore}
          hasMore={listData.hasNextPage}
          hasActiveFilters={hasFilters}
          onLoadMore={listData.loadMore}
          onSortChange={filters.setSortState}
          onToggleGroup={tableRows.toggleGroup}
          onOpenTrace={navigation.openTrace}
          onOpenTraceContextMenu={navigation.openTraceContextMenu}
        />

        <TracesDeleteDialog
          isOpen={actions.isDeleteDialogOpen}
          loading={actions.isDeleting}
          count={actions.pendingDeleteCount}
          onOpenChange={(open) => {
            if (!open) {
              actions.cancelDelete();
            }
          }}
          onConfirm={() => void actions.confirmDelete()}
        />
      </ListPageLayout>
    </ExecutionsTableContextProvider>
  );
}
